import { RefObject, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { isLapsedBlockedAction } from "@/lib/lapsedAccess";

/**
 * Banner plus view-only enforcement for a nursery whose subscription has ended.
 * The database already refuses every change; this hides the controls so
 * Children and Staff read as view only.
 */
export function LapsedViewOnly({ mainRef }: { mainRef: RefObject<HTMLElement | null> }) {
  const location = useLocation();
  useEffect(() => {
    const root = mainRef.current;
    if (!root || location.pathname === "/records" || location.pathname === "/settings" || location.pathname === "/upgrade") return;
    const hide = () => {
      root.querySelectorAll<HTMLElement>("button, a[role=button], input[type=file], [role=menuitem]").forEach((el) => {
        const label = `${el.innerText ?? ""} ${el.getAttribute("aria-label") ?? ""} ${el.getAttribute("title") ?? ""}`.trim();
        if (el.getAttribute("role") === "tab") return;
        if (label && isLapsedBlockedAction(label)) el.style.display = "none";
      });
      root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("form input, form textarea, form select").forEach((el) => { el.disabled = true; });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(root, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [mainRef, location.pathname]);

  return (
    <div className="border-b border-nursly-stone bg-nursly-cream px-4 py-3 text-sm text-nursly-ink flex flex-wrap items-center gap-3 justify-between">
      <span>Your subscription has ended. You can view your children and staff and download your records. Subscribe again to use everything else.</span>
      <div className="flex gap-2">
        <Button asChild variant="outline" size="sm" className="min-h-11"><Link to="/records">Download your records</Link></Button>
        <Button asChild size="sm" className="min-h-11"><Link to="/settings?tab=billing">Plan &amp; Billing</Link></Button>
      </div>
    </div>
  );
}
